import { create } from "zustand";
import { persist } from "zustand/middleware";
import { applySyntaxTheme } from "../theme/syntax-themes";

export type Theme = "light" | "dark" | "system";

function resolveTheme(theme: Theme): "light" | "dark" {
  if (theme === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return theme;
}

function applyTheme(theme: Theme, editorTheme?: string) {
  const resolved = resolveTheme(theme);
  document.documentElement.setAttribute("data-theme", resolved);
  if (editorTheme) {
    applySyntaxTheme(editorTheme, resolved);
  }
}

interface PanelState {
  sidebarOpen: boolean;
  sidebarWidth: number;
  bottomPanelOpen: boolean;
  bottomPanelHeight: number;
  editorPreviewRatio: number;
  focusMode: boolean;
}

export type DashboardViewMode = "grid" | "list";
export type DashboardSortKey = "modified" | "created" | "alpha-asc" | "alpha-desc";

export type BottomPanelTab = "errors" | "logs" | "outline" | "summary" | "versions" | "vitals";
export type SidebarTab = "files" | "outline" | "comments" | "chat" | "citations" | "context";
export type ToastStyle = "normal" | "minimal";
export type SelectionToolbarAnchor = "start" | "end";

interface UIState extends PanelState {
  theme: Theme;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  _syncResolvedTheme: () => void;

  dashboardSidebarCollapsed: boolean;
  setDashboardSidebarCollapsed: (collapsed: boolean) => void;
  toggleDashboardSidebar: () => void;

  dashboardViewMode: DashboardViewMode;
  setDashboardViewMode: (mode: DashboardViewMode) => void;

  dashboardSortKey: DashboardSortKey;
  setDashboardSortKey: (key: DashboardSortKey) => void;

  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setSidebarWidth: (width: number) => void;

  setBottomPanelOpen: (open: boolean) => void;
  toggleBottomPanel: () => void;
  setBottomPanelHeight: (height: number) => void;

  bottomPanelTab: BottomPanelTab;
  setBottomPanelTab: (tab: BottomPanelTab) => void;
  openBottomPanelTab: (tab: BottomPanelTab) => void;

  sidebarTab: SidebarTab;
  setSidebarTab: (tab: SidebarTab) => void;
  openSidebarTab: (tab: SidebarTab) => void;

  setEditorPreviewRatio: (ratio: number) => void;

  setFocusMode: (enabled: boolean) => void;
  toggleFocusMode: () => void;

  editorTheme: string;
  setEditorTheme: (themeId: string) => void;

  toastStyle: ToastStyle;
  setToastStyle: (style: ToastStyle) => void;

  selectionToolbarAnchor: SelectionToolbarAnchor;
  setSelectionToolbarAnchor: (anchor: SelectionToolbarAnchor) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      theme: "dark",
      resolvedTheme: "dark",
      sidebarOpen: true,
      sidebarWidth: 240,
      bottomPanelOpen: false,
      bottomPanelHeight: 200,
      editorPreviewRatio: 0.5,
      focusMode: false,
      bottomPanelTab: "errors" as BottomPanelTab,
      dashboardSidebarCollapsed: false,
      dashboardViewMode: "grid" as DashboardViewMode,
      dashboardSortKey: "modified" as DashboardSortKey,
      editorTheme: "lever",
      toastStyle: "normal" as ToastStyle,
      selectionToolbarAnchor: "start" as SelectionToolbarAnchor,

      setTheme: (theme) => {
        const { editorTheme } = get();
        applyTheme(theme, editorTheme);
        set({ theme, resolvedTheme: resolveTheme(theme) });
      },
      toggleTheme: () =>
        set((state) => {
          const order: Theme[] = ["dark", "light", "system"];
          const next = order[(order.indexOf(state.theme) + 1) % order.length];
          applyTheme(next, state.editorTheme);
          return { theme: next, resolvedTheme: resolveTheme(next) };
        }),
      _syncResolvedTheme: () => {
        const { theme, editorTheme } = get();
        const resolved = resolveTheme(theme);
        applyTheme(theme, editorTheme);
        set({ resolvedTheme: resolved });
      },

      setDashboardSidebarCollapsed: (collapsed) => set({ dashboardSidebarCollapsed: collapsed }),
      toggleDashboardSidebar: () => set((s) => ({ dashboardSidebarCollapsed: !s.dashboardSidebarCollapsed })),

      setDashboardViewMode: (mode) => set({ dashboardViewMode: mode }),

      setDashboardSortKey: (key) => set({ dashboardSortKey: key }),

      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      setSidebarWidth: (width) => set({ sidebarWidth: Math.max(180, Math.min(400, width)) }),

      setBottomPanelOpen: (open) => set({ bottomPanelOpen: open }),
      toggleBottomPanel: () => set((s) => ({ bottomPanelOpen: !s.bottomPanelOpen })),
      setBottomPanelHeight: (height) => set({ bottomPanelHeight: Math.max(100, Math.min(500, height)) }),

      setBottomPanelTab: (tab) => set({ bottomPanelTab: tab }),
      openBottomPanelTab: (tab) => set({ bottomPanelOpen: true, bottomPanelTab: tab }),

      sidebarTab: "files" as SidebarTab,
      setSidebarTab: (tab) => set({ sidebarTab: tab }),
      openSidebarTab: (tab) => set({ sidebarOpen: true, sidebarTab: tab }),

      setEditorPreviewRatio: (ratio) => set({ editorPreviewRatio: Math.max(0.2, Math.min(0.8, ratio)) }),

      setFocusMode: (enabled) => set({ focusMode: enabled }),
      toggleFocusMode: () => set((s) => ({ focusMode: !s.focusMode })),

      setEditorTheme: (themeId) => {
        const { theme } = get();
        applySyntaxTheme(themeId, resolveTheme(theme));
        set({ editorTheme: themeId });
      },

      setToastStyle: (style) => set({ toastStyle: style }),

      setSelectionToolbarAnchor: (anchor) => set({ selectionToolbarAnchor: anchor }),
    }),
    {
      name: "lever-ui",
      partialize: (state) => ({
        theme: state.theme,
        editorTheme: state.editorTheme,
        sidebarOpen: state.sidebarOpen,
        sidebarWidth: state.sidebarWidth,
        bottomPanelOpen: state.bottomPanelOpen,
        bottomPanelHeight: state.bottomPanelHeight,
        editorPreviewRatio: state.editorPreviewRatio,
        dashboardSidebarCollapsed: state.dashboardSidebarCollapsed,
        dashboardViewMode: state.dashboardViewMode,
        dashboardSortKey: state.dashboardSortKey,
        toastStyle: state.toastStyle,
        selectionToolbarAnchor: state.selectionToolbarAnchor,
      }),
    },
  ),
);
